/** Browser entry: hydrate (or mount) the React app. */

import React from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
  type DehydratedState,
} from '@tanstack/react-query';

import App from './App';
import { getAuthStore } from '@/stores/authStore';
import type { User } from '@/lib/types';
import './styles/globals.css';

declare global {
  interface Window {
    __SSR__?: boolean;
    __INSTAMOUSE_USER__?: User | null;
    __REACT_QUERY_STATE__?: unknown;
  }
}

// Seed the auth store from any SSR-injected user before first render.
if (typeof window !== 'undefined' && window.__INSTAMOUSE_USER__) {
  getAuthStore(window.__INSTAMOUSE_USER__);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const container = document.getElementById('root');
const dehydratedState = window.__REACT_QUERY_STATE__ as DehydratedState | undefined;

const tree = (
  <QueryClientProvider client={queryClient}>
    <HydrationBoundary state={dehydratedState}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </HydrationBoundary>
  </QueryClientProvider>
);

if (container) {
  if (window.__SSR__ && container.hasChildNodes()) {
    hydrateRoot(container, tree);
  } else {
    createRoot(container).render(tree);
  }
}
